import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select, message } from 'antd';
import {
  createPlanFeature,
  getPermissionApis,
  type ApiResource
} from './permissionManageData';

const FEATURE_CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
const RESOURCE_CODE_PATTERN = /^[0-9]{21}$/;

type CreateFormValues = {
  resourceCode: string;
  featureCode: string;
  displayName: string;
};

type PermissionCreateModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

function apiLabel(item: ApiResource) {
  const path = [item.httpMethod, item.pathPattern].filter(Boolean).join(' ');
  const status = item.status ? ` · ${item.status}` : '';
  return `${path || '接口'} · ${item.resourceCode || ''}${status}`;
}

export function PermissionCreateModal({ open, onClose, onCreated }: PermissionCreateModalProps) {
  const [form] = Form.useForm<CreateFormValues>();
  const [apis, setApis] = useState<ApiResource[]>([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    let cancelled = false;
    setApiLoading(true);
    const load = async () => {
      const collected: ApiResource[] = [];
      for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
        const res = await getPermissionApis({ pageNum, pageSize: 100 });
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || '接口列表加载失败');
          setApis([]);
          return;
        }
        const list = res.content?.records;
        collected.push(...(Array.isArray(list) ? list.filter((item) => item.resourceCode) : []));
        if (!Array.isArray(list) || list.length < 100) break;
      }
      if (!cancelled) setApis(collected);
    };
    load()
      .catch(() => {
        if (!cancelled) {
          message.error('接口列表加载失败');
          setApis([]);
        }
      })
      .finally(() => {
        if (!cancelled) setApiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, form]);

  const submit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values?.resourceCode || !values.featureCode || !values.displayName) return;
    const body = {
      resourceCode: values.resourceCode.trim(),
      featureCode: values.featureCode.trim(),
      displayName: values.displayName.trim()
    };
    setSubmitting(true);
    try {
      const res = await createPlanFeature(body);
      if (res.code !== 200) {
        message.error(res.message || '新增权限失败');
        return;
      }
      message.success(res.message || '已新增');
      onCreated();
      onClose();
    } catch {
      message.error('新增权限失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title='新增权限'
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={720}
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            取消
          </Button>
          <Button type='primary' className='rounded-lg' loading={submitting} onClick={submit}>
            确认
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout='horizontal'
        labelCol={{ flex: '110px' }}
        wrapperCol={{ flex: 1 }}
        labelAlign='right'
        colon
        requiredMark={(labelNode, { required }) => (
          <>
            {labelNode}
            {required ? <span className='admin-perm-required'>*</span> : null}
          </>
        )}
        className='pt-2 admin-perm-form'
      >
        <Form.Item
          name='resourceCode'
          label='绑定接口'
          rules={[
            { required: true, message: '请选择要绑定的接口' },
            { pattern: RESOURCE_CODE_PATTERN, message: '接口编码须为 21 位数字' }
          ]}
        >
          <Select
            showSearch
            allowClear
            loading={apiLoading}
            placeholder='选择已启用且未绑定的接口'
            optionFilterProp='label'
            options={apis.map((item) => ({
              value: item.resourceCode,
              label: apiLabel(item)
            }))}
            notFoundContent={apiLoading ? '加载中' : '暂无可选接口'}
          />
        </Form.Item>
        <Form.Item
          name='featureCode'
          label='功能标识'
          getValueFromEvent={(event) => String(event.target.value || '').toUpperCase()}
          rules={[
            { required: true, message: '请输入功能标识' },
            {
              pattern: FEATURE_CODE_PATTERN,
              message: '大写字母开头，仅含大写字母、数字或下划线，1-64 位'
            }
          ]}
        >
          <Input placeholder='例如 EMAIL_SEND' maxLength={64} allowClear />
        </Form.Item>
        <Form.Item
          name='displayName'
          label='功能名称'
          rules={[
            { required: true, whitespace: true, message: '请输入功能名称' },
            { max: 128, message: '最多 128 字' }
          ]}
        >
          <Input placeholder='请输入功能名称' maxLength={128} allowClear />
        </Form.Item>
      </Form>
    </Modal>
  );
}
